// Resolve the admin page background color from the tenant theme settings,
// mirroring the storefront + ThemeManager logic exactly so the admin chrome
// matches the storefront:
//   - pageBackgroundMode 'custom' -> theme.pageBackgroundColor (default #f5f3ff)
//   - otherwise ('auto')          -> menuColor mixed 75% toward white
//
// This intentionally reads from the saved theme (GET /admin/theme) rather than
// hardcoding a value, so each tenant's admin matches their storefront.

const DEFAULT_MENU_COLOR = '#6236ff';
const DEFAULT_PAGE_BACKGROUND = '#f5f3ff';

export function normalizeHex(value) {
  if (typeof value !== 'string') return null;
  const hex = value.trim();
  if (!hex.startsWith('#')) return null;
  const raw = hex.slice(1);
  if (raw.length === 3) {
    return `#${raw
      .split('')
      .map((ch) => `${ch}${ch}`)
      .join('')}`;
  }
  if (raw.length === 6) return `#${raw}`;
  return null;
}

export function mixHex(hex, otherHex, amount) {
  const toRgb = (input) => {
    const normalized = normalizeHex(input);
    if (!normalized) return null;
    const intVal = parseInt(normalized.slice(1), 16);
    return {
      r: (intVal >> 16) & 255,
      g: (intVal >> 8) & 255,
      b: intVal & 255,
    };
  };

  const a = toRgb(hex);
  const b = toRgb(otherHex);
  if (!a || !b) return hex;
  const t = Math.min(1, Math.max(0, amount));
  const r = Math.round(a.r + (b.r - a.r) * t);
  const g = Math.round(a.g + (b.g - a.g) * t);
  const bb = Math.round(a.b + (b.b - a.b) * t);
  return `#${[r, g, bb].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Compute the page background color from a tenant theme object.
 * @param {object} theme - the theme as returned by GET /admin/theme
 * @returns {string} a CSS color value
 */
export function resolvePageBackground(theme) {
  const t = theme && typeof theme === 'object' ? theme : {};
  const mode = String(t.pageBackgroundMode || 'auto').toLowerCase();
  const menuBaseColor = normalizeHex(t.menuColor) || DEFAULT_MENU_COLOR;

  if (mode === 'custom') {
    return normalizeHex(t.pageBackgroundColor) || DEFAULT_PAGE_BACKGROUND;
  }
  // 'auto' (default): a light tint of the tenant menu color, same as storefront.
  return mixHex(menuBaseColor, '#ffffff', 0.75);
}

/**
 * Relative luminance (WCAG) of a hex color, 0 (black) .. 1 (white).
 */
function relativeLuminance(hex) {
  const normalized = normalizeHex(hex);
  if (!normalized) return 0;
  const intVal = parseInt(normalized.slice(1), 16);
  const srgb = [(intVal >> 16) & 255, (intVal >> 8) & 255, intVal & 255].map((c) => {
    const x = c / 255;
    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.4152 * srgb[2];
}

/**
 * Resolve the admin PRIMARY BUTTON color the same way the storefront does: it uses
 * `theme.menuColor` directly as the button/brand background (e.g. the storefront's
 * "Send Code" / cart CTAs set `background: menuColor`). Mirrors that so the admin's
 * primary action buttons match the configured storefront brand color.
 * @param {object} theme - the theme as returned by GET /admin/theme
 * @returns {string} a CSS color value
 */
export function resolveButtonColor(theme) {
  const t = theme && typeof theme === 'object' ? theme : {};
  return normalizeHex(t.menuColor) || DEFAULT_MENU_COLOR;
}

/**
 * A readable foreground color (white or near-black) for text/icons placed on top of
 * the resolved button color, chosen by luminance so a light brand color still has
 * legible text. Mirrors the storefront, which pairs menuColor backgrounds with white.
 * @param {string} buttonColor
 * @returns {string}
 */
export function resolveButtonTextColor(buttonColor) {
  // Threshold ~0.6: brand colors are usually saturated mid-tones that read best with
  // white text; only quite light backgrounds flip to dark ink.
  return relativeLuminance(buttonColor) > 0.6 ? '#1f1142' : '#ffffff';
}

/**
 * `rgba(r,g,b,a)` string for a hex color at the given alpha. Used to build the light
 * brand TINTS (selected/hover chip backgrounds) from the tenant brand color so they
 * track the theme instead of a hardcoded violet tint.
 */
export function hexToRgba(hex, alpha) {
  const n = normalizeHex(hex);
  if (!n) return `rgba(124, 58, 237, ${alpha})`;
  const v = parseInt(n.slice(1), 16);
  return `rgba(${(v >> 16) & 255}, ${(v >> 8) & 255}, ${v & 255}, ${alpha})`;
}

/**
 * The full set of admin brand CSS-variable values derived from the tenant theme's
 * `menuColor`, mirroring the storefront. Returned as a flat map of CSS custom property
 * name -> value so App.js can set them all on :root in one place. Every admin brand
 * accent (buttons, active tabs/toggles/chips, the header gradient, light tints, brand
 * text) reads these variables, so swapping the tenant theme recolors the whole admin.
 */
export function resolveBrandVars(theme) {
  const brand = resolveButtonColor(theme); // theme.menuColor (default #6236ff)
  const brandText = resolveButtonTextColor(brand);
  // A deeper shade for hover/pressed, mixed toward black (same recipe the storefront
  // uses for its menu-hover border).
  const brandStrong = mixHex(brand, '#000000', 0.14);
  // Brand text that stays legible on a LIGHT (tinted/white) surface: the brand color
  // itself, nudged darker so it reads as text rather than a pale wash.
  const brandInk = mixHex(brand, '#000000', 0.25);
  return {
    '--admin-button-color': brand,
    '--admin-button-text': brandText,
    '--admin-brand': brand,
    '--admin-brand-strong': brandStrong,
    '--admin-brand-text': brandText,
    '--admin-brand-ink': brandInk,
    // Light tints for selected/hover chip + badge backgrounds and their borders.
    '--admin-brand-tint': hexToRgba(brand, 0.12),
    '--admin-brand-tint-soft': hexToRgba(brand, 0.06),
    '--admin-brand-tint-border': hexToRgba(brand, 0.35),
    // A two-stop gradient for the header/nav/login chrome, derived from the brand color.
    '--admin-brand-gradient': `linear-gradient(135deg, ${mixHex(brand, '#ffffff', 0.12)} 0%, ${brandStrong} 100%)`,
  };
}

export { DEFAULT_MENU_COLOR, DEFAULT_PAGE_BACKGROUND };
